import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle, AlertCircle, Clock, XCircle, Wifi, WifiOff } from 'lucide-react';
import LinkedInConnect from './LinkedInConnect';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  connected: { label: 'Connected', icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-900/20' },
  pending: { label: 'Pending Setup', icon: Clock, color: 'text-amber-400', bg: 'bg-amber-50' },
  expired: { label: 'Token Expired', icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-900/20' },
  error: { label: 'Error', icon: XCircle, color: 'text-red-400', bg: 'bg-red-900/20' },
  not_configured: { label: 'Not Connected', icon: WifiOff, color: 'text-gray-500', bg: 'bg-gray-800/50' },
};

const PLATFORM_COLORS = {
  facebook: '#1877f2', instagram: '#e4405f', linkedin: '#0a66c2', tiktok: '#000000',
  gbp: '#4285f4', youtube: '#ff0000', rumble: '#85c742', truth_social: '#5448ee',
};

export default function ClientConnections({ client, getHeaders }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/connections`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setConnections(d.connections || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">Loading connections...</div>;

  const connected = connections.filter(c => c.status === 'connected').length;

  return (
    <div className="space-y-5" data-testid="connections-tab">
      {/* Summary */}
      <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg px-5 py-4">
        <div className="p-2.5 rounded-xl bg-blue-900/30">
          <Wifi className="h-5 w-5 text-blue-400" />
        </div>
        <div>
          <p className="text-gray-900 font-semibold text-sm">Platform Connections</p>
          <p className="text-xs text-gray-400">{connected} of {connections.length} platforms connected</p>
        </div>
        <div className="ml-auto">
          <Badge className={`text-xs ${connected === connections.length ? 'bg-green-50 text-green-400' : 'bg-amber-900/30 text-amber-400'}`}>
            {connected === connections.length ? 'All Connected' : `${connections.length - connected} Pending`}
          </Badge>
        </div>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {connections.map(conn => {
          const cfg = STATUS_CONFIG[conn.status] || STATUS_CONFIG.not_configured;
          const Icon = cfg.icon;
          const platformColor = PLATFORM_COLORS[conn.platform] || '#6b7280';
          return (
            <Card key={conn.platform} className="bg-white border-gray-200 hover:border-gray-200 transition-colors" data-testid={`connection-${conn.platform}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${platformColor}20` }}>
                    <span className="text-xs font-bold" style={{ color: platformColor }}>
                      {conn.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-medium truncate">{conn.name}</p>
                    {conn.email && <p className="text-[10px] text-gray-500 truncate">{conn.email}</p>}
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${cfg.bg}`}>
                  <Icon className={`h-3 w-3 ${cfg.color}`} />
                  <span className={`text-xs ${cfg.color}`}>{cfg.label}</span>
                </div>
                {conn.updated_at && (
                  <p className="text-[10px] text-gray-600 mt-2">Updated: {new Date(conn.updated_at).toLocaleDateString()}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Self-service LinkedIn Company Page connect + post */}
      <LinkedInConnect getHeaders={getHeaders} />

      {/* Contact Admin Note */}
      <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg px-4 py-3">
        <p className="text-xs text-blue-300">
          Platform connections are managed by your account team. Contact your account manager to connect new platforms or refresh expired tokens.
        </p>
      </div>
    </div>
  );
}
